import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/hooks/useStore';
import { useLocation } from 'react-router-dom';
import { isPathEk } from '@/utils/routes';
import { LANGUAGES_SUPPORTED } from '@/types/enums';
import { getReservationReception } from '@/utils/utils';
import AccessoryItem from '../components/AccessoryItem';
import { SubTitleWithBox } from '@/components/general/title/sub-title/SubTitle';
import { IconETC, IconSmartphoneHolder, IconTopBox, IconUsbCharger } from '@/components/icon';
import CalenderSelectBike from '../components/calender-select-bike/CalenderSelectBike';

export default observer(function OverviewTab() {
    // hook
    const {
        t,
        i18n: { language }
    } = useTranslation();
    const { pathname } = useLocation();

    const isEk = isPathEk(pathname);

    // store
    const {
        bookingStore: { bikeBookingDetail }
    } = useStore();

    const getBikeManufacture = (bike: any, isEk: boolean): string => {
        if (isEk) {
            return bike?.bikeManufacture?.id;
        }

        if (language === LANGUAGES_SUPPORTED.Japanese) {
            return bike?.bikeManufacture?.name_jp || '--';
        } else {
            return bike?.bikeManufacture?.name || '--';
        }
    };

    return (
        <div>
            <div className='flex flex-col items-center gap-[min(40px,7.7vw)]'>
                {/* Table infor bike */}
                <div className='leading-lh-nor14 text-fs-mid14 font-normal w-full grid gap-0 grid-cols-1 md:grid-cols-2'>

                    <div className='grid grid-cols-2 items-center border border-border-gray2'>
                        <div className='col-span-1 px-[min(30px,2.5vw)] py-[min(15px,2.5vw)] flex break-words items-center justify-end bg-[#F8F7F1] text-left border-r border-border-gray2 h-full font-medium min-h-[40px]'>
                            {t('booking_bike.field.manufacturer')}
                        </div>
                        <div className='col-span-1 px-[min(10px,2vw)] py-[min(15px,2.5vw)] flex items-center justify-center text-center min-h-[40px]'>
                            {getBikeManufacture(bikeBookingDetail, isEk)}
                        </div>
                    </div>

                    <div className='grid grid-cols-2 items-center border border-border-gray2'>
                        <div className='col-span-1 px-[min(30px,2.5vw)] py-[min(15px,2.5vw)] flex break-words items-center justify-end bg-[#F8F7F1] text-left border-r border-border-gray2 h-full font-medium min-h-[40px]'>
                            {t('booking_bike.field.model_name')}
                        </div>
                        <div className='col-span-1 px-[min(10px,2vw)] py-[min(15px,2.5vw)] flex items-center justify-center text-center min-h-[40px]'>
                            {bikeBookingDetail?.bikeModel || '--'}
                        </div>
                    </div>

                    {!isEk && (
                        <>
                            <div className='grid grid-cols-2 items-center border border-border-gray2'>
                                <div className='col-span-1 px-[min(30px,2.5vw)] py-[min(15px,2.5vw)] flex break-words items-center justify-end bg-[#F8F7F1] text-left border-r border-border-gray2 h-full font-medium min-h-[40px]'>
                                    {t('booking_bike.field.engine_size')}
                                </div>
                                <div className='col-span-1 px-[min(10px,2vw)] py-[min(15px,2.5vw)] flex items-center justify-center text-center min-h-[40px]'>
                                    {bikeBookingDetail?.bikeCapacity?.name || '--'}
                                </div>
                            </div>
                            <div className='grid grid-cols-2 items-center border border-border-gray2'>
                                <div className='col-span-1 px-[min(30px,2.5vw)] py-[min(15px,2.5vw)] flex break-words items-center justify-end bg-[#F8F7F1] text-left border-r border-border-gray2 h-full font-medium min-h-[40px]'>
                                    {t('booking_bike.field.gearbox')}
                                </div>
                                <div className='col-span-1 px-[min(10px,2vw)] py-[min(15px,2.5vw)] flex items-center justify-center text-center min-h-[40px]'>
                                    {bikeBookingDetail?.bikeTransmission || '--'}
                                </div>
                            </div>
                        </>
                    )}

                    <div className='grid grid-cols-2 items-center border border-border-gray2'>
                        <div className='col-span-1 px-[min(30px,2.5vw)] py-[min(15px,2.5vw)] flex break-words items-center justify-end bg-[#F8F7F1] text-left border-r border-border-gray2 h-full font-medium min-h-[40px]'>
                            {t('booking_bike.field.bike_type')}
                        </div>
                        <div className='col-span-1 px-[min(10px,2vw)] py-[min(15px,2.5vw)] flex items-center justify-center text-center min-h-[40px]'>
                            {
                                language === LANGUAGES_SUPPORTED.Japanese ?
                                    bikeBookingDetail?.bikeType?.name_jp || '--' :
                                    bikeBookingDetail?.bikeType?.name || '--'
                            }
                        </div>
                    </div>

                    <div className='grid grid-cols-2 items-center border border-border-gray2'>
                        <div className='col-span-1 px-[min(30px,2.5vw)] py-[min(15px,2.5vw)] flex break-words items-center justify-end bg-[#F8F7F1] text-left border-r border-border-gray2 h-full font-medium'>
                            予約受付
                        </div>
                        <div className='col-span-1 px-[min(10px,2vw)] py-[min(15px,2.5vw)] flex items-center justify-center text-center'>
                            {getReservationReception(bikeBookingDetail?.bookingLeadTime, t)}
                        </div>
                    </div>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-3 gap-[min(40px,7.7vw)]'>
                    {/* Non rental date */}
                    <div className='col-span-1 md:col-span-2 flex flex-col gap-[20px]'>
                        <SubTitleWithBox title={t('booking_bike.field.non_rental_date')} />
                        <div>
                            <CalenderSelectBike />
                        </div>
                    </div>

                    {/* Equipment GAS */}
                    {!isEk && (
                        <div className='flex flex-col gap-[20px]'>
                            <SubTitleWithBox title={t('booking_bike.field.equipment')} />
                            <div className='flex flex-wrap gap-2.5'>
                                <AccessoryItem
                                    icon={<IconSmartphoneHolder />}
                                    text={t('booking_bike.field.smartphone_holder')}
                                    value={bikeBookingDetail?.isSmartphoneHolder}
                                />
                                <AccessoryItem
                                    icon={<IconETC />}
                                    text={t('booking_bike.field.etc')}
                                    value={bikeBookingDetail?.isEtc}
                                />
                                <AccessoryItem
                                    icon={<IconUsbCharger />}
                                    text={t('booking_bike.field.usb_charging')}
                                    value={bikeBookingDetail?.isUsbCharging}
                                />
                                <AccessoryItem
                                    icon={<IconTopBox />}
                                    text={t('booking_bike.field.top_box')}
                                    value={bikeBookingDetail?.isTopBox}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
});
